/**
 * Response helpers shared by the API modules.
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165).
 */
import * as client from '@brainfocus/web/api/client';
import { assert } from '@brainfocus/web/utils/assert';
import { map, merge, Observable, scan, share, toArray } from 'rxjs';
import { sum } from 'simple-statistics';

/**
 * Send request and return observable that emits progress object until it
 * finishes in which case it emits response as a buffer.
 */
export function fetchReader(
    method: string,
    url: string,
    data?: FormData | Record<string, any>,
): Observable<ArrayBuffer | { progress: number }> {
    const chunks$ = new Observable<{ result: Uint8Array; progress: number }>((sub) => {
        let cancel: (() => void) | undefined;

        client.execFetch(method, url, data).then((response) => {
            const clHeader = response.headers.get('Content-Length');
            const contentLength = +clHeader!;
            assert(Number.isFinite(contentLength), `Invalid content length ${clHeader}`);
            assert(response.body, 'Response has no body');
            const reader = response.body.getReader();
            cancel = () => reader.cancel();
            return resozReader(reader, contentLength);
        }).catch(e => sub.error(e));

        return () => cancel?.();

        async function resozReader(reader: ReadableStreamDefaultReader<Uint8Array>, contentLength: number) {
            const result = await reader!.read();
            if (result.value) {
                sub.next({ result: result.value, progress: result.value.length / contentLength });
            }
            if (result.done) {
                sub.complete();
            } else {
                resozReader(reader, contentLength);
            }
        }
    }).pipe(share());
    const progress$ = chunks$.pipe(
        scan((progress, part) => progress + part.progress, 0),
        map(progress => ({ progress })),
    );
    const result$ = chunks$.pipe(
        toArray(),
        map((chunks) => {
            const totalSize = sum(chunks.map(chunk => chunk.result.length));
            const out = new Uint8Array(totalSize);
            let offset = 0;
            for (const chunk of chunks) {
                out.set(chunk.result, offset);
                offset += chunk.result.length;
            }
            return out.buffer;
        }),
    );
    return merge(result$, progress$);
}

/**
 * Assert the response has the object shape the caller expects.
 *
 * Reports the SHAPE only: interpolating the rejected value used to dump the
 * whole server response — a login page with its CSRF token, feature flags
 * and changelog JSON — into the error message, which callers then rendered
 * to the user (#865).
 */
export function assertPojo<T extends object>(data: object | ArrayBuffer | string): asserts data is T {
    assert(typeof data !== 'string', 'Unexpected response: expected a JSON object, received text');
    assert(!(data instanceof ArrayBuffer), 'Unexpected response: expected a JSON object, received binary data');
}
